import { useState } from 'react';
import { FolderTree, Layers, Package, TrendingUp } from 'lucide-react';

import ChartCard from '@/features/reports/components/ChartCard';
import RankBarChart from '@/features/reports/components/charts/RankBarChart';
import { SimpleKpi } from '@/features/reports/components/KpiTile';
import ReportTable from '@/features/reports/components/ReportTable';
import { useReportOutlet } from '@/features/reports/hooks/useReportOutlet';
import { useCategoryPerformance } from '@/features/reports/hooks/useReports';
import {
  formatDecimal,
  formatNumber,
  formatRangeCaption,
  formatToman,
} from '@/features/reports/lib/format';
import { persianDisplayText } from '@/lib/persianUi';

/** Category performance, with direct vs rolled-up (including descendants) revenue. */
export default function CategoriesReport() {
  const { query, filters } = useReportOutlet();
  const [basis, setBasis] = useState('rollup');

  const categories = useCategoryPerformance(query, { limit: 100 });
  const rows = categories.data?.rows ?? [];

  const valueKey = basis === 'rollup' ? 'rollup_revenue' : 'direct_revenue';
  const unitsKey = basis === 'rollup' ? 'rollup_units' : 'direct_units';
  const list = [...rows].sort((a, b) => Number(b[valueKey] ?? 0) - Number(a[valueKey] ?? 0));

  const chartRows = list.slice(0, 12).map((row) => ({
    ...row,
    label: persianDisplayText(row.name_fa, 'نام ثبت‌نشده'),
  }));

  const topCategory = list[0];

  return (
    <div className="reports-page">
      <div className="report-section-head">
        <h2 className="section-title">عملکرد دسته‌بندی‌ها</h2>
        <p className="report-section-caption">
          {formatRangeCaption(filters.from, filters.to)} •{' '}
          {basis === 'rollup' ? 'با احتساب زیردسته‌ها' : 'فقط فروش مستقیم این دسته'}
        </p>
      </div>

      <div className="kpi-grid report-kpi-grid">
        <SimpleKpi
          title="درآمد کل دسته‌ها"
          value={categories.data?.total_revenue}
          format={formatToman}
          icon={TrendingUp}
          accent="green"
        />
        <SimpleKpi
          title="تعداد دسته"
          value={rows.length}
          format={formatNumber}
          icon={FolderTree}
          accent="blue"
        />
        <SimpleKpi
          title="دسته برتر"
          value={topCategory ? topCategory[valueKey] : 0}
          format={formatToman}
          icon={Layers}
          accent="amber"
          secondary={persianDisplayText(topCategory?.name_fa)}
        />
        <SimpleKpi
          title="سهم دسته برتر"
          value={topCategory?.revenue_share_pct}
          format={(value) => formatDecimal(value, 1) + '٪'}
          icon={Package}
          accent="purple"
        />
      </div>

      <ChartCard
        title="دسته‌های برتر"
        subtitle="۱۲ دسته نخست بر پایه مبنای انتخابی"
        actions={
          <div className="report-segmented">
            <button
              type="button"
              className={`report-segment${basis === 'rollup' ? ' active' : ''}`}
              onClick={() => setBasis('rollup')}
            >
              با زیردسته‌ها
            </button>
            <button
              type="button"
              className={`report-segment${basis === 'direct' ? ' active' : ''}`}
              onClick={() => setBasis('direct')}
            >
              فقط مستقیم
            </button>
          </div>
        }
        height={420}
        isLoading={categories.isLoading}
        isError={categories.isError}
        error={categories.error}
        isEmpty={!list.length}
      >
        <RankBarChart
          rows={chartRows}
          valueKey={valueKey}
          money
          height={420}
          yAxisWidth={170}
        />
      </ChartCard>

      <ChartCard
        title="جدول دسته‌بندی‌ها"
        subtitle="تودرتویی دسته‌ها با تورفتگی نشان داده شده است"
        bodyClassName="report-card-body-table"
        isLoading={categories.isLoading}
        isError={categories.isError}
        error={categories.error}
        isEmpty={!list.length}
      >
        <ReportTable
          rows={list}
          rowKey={(row) => row.category_id}
          maxHeight={560}
          columns={[
            {
              key: 'name',
              header: 'دسته',
              render: (row) => (
                <span
                  className="report-cell-tree"
                  style={{ paddingInlineStart: `${Math.min(row.depth ?? 0, 6) * 18}px` }}
                >
                  <span className="report-cell-primary">{persianDisplayText(row.name_fa, 'نام ثبت‌نشده')}</span>
                </span>
              ),
            },
            {
              key: 'direct_revenue',
              header: 'درآمد مستقیم',
              render: (row) => formatToman(row.direct_revenue),
            },
            {
              key: 'rollup_revenue',
              header: 'درآمد با زیردسته',
              render: (row) => formatToman(row.rollup_revenue),
            },
            {
              key: 'units',
              header: 'کالا',
              render: (row) => formatNumber(row[unitsKey]),
            },
            { key: 'orders', header: 'سفارش', render: (row) => formatNumber(row.orders) },
            {
              key: 'product_count',
              header: 'کالا در دسته',
              render: (row) => (
                <span className="report-cell-secondary">
                  {formatNumber(row.product_count)} / {formatNumber(row.rollup_product_count)}
                </span>
              ),
            },
            {
              key: 'revenue_share_pct',
              header: 'سهم',
              render: (row) => formatDecimal(row.revenue_share_pct, 1) + '٪',
            },
          ]}
        />
      </ChartCard>
    </div>
  );
}
